import React, { useState } from "react";
import axios from "axios";
import CtaPickup from "./CTA-Pickup";
import { MapPin } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceabilityPage = () => {
    const [pincode, setPincode] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const handleCheckServiceability = async () => {
        if (!pincode || pincode.length !== 6) {
            setError("Please enter a valid 6-digit pincode");
            setResult(null);
            return;
        }

        setLoading(true);
        setError("");
        setResult(null);

        try {
            const response = await axios.get(`${API_URL}serviceability?searchpincode=${pincode}`);
            if (response.data && response.data.data && response.data.data.length > 0) {
                setResult(response.data.data[0]);
            } else {
                setError("Service not available for this pincode");
            }
        } catch (err) {
            console.error(err);
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="bg-gradient-to-b from-yellow-50 via-white to-white flex flex-col items-center">
                {/* Hero Section */}
                <div className="w-full max-w-4xl px-6 py-16 text-center">
                    <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-800 mb-4">
                        Check Serviceability
                    </h1>
                    <p className="text-gray-600 text-lg sm:text-xl mb-10">
                        Enter your pincode to see if our services are available in your area.
                    </p>

                    {/* Input + Button */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <div className="relative w-full sm:flex-1">
                            <input
                                type="text"
                                placeholder="Enter 6-digit pincode"
                                value={pincode}
                                onChange={(e) => setPincode(e.target.value)}
                                className="w-full px-5 py-4 pr-12 rounded-xl border border-gray-300 shadow-sm focus:outline-none focus:ring-4 focus:ring-yellow-300 focus:border-yellow-400 transition"
                            />
                            <MapPin className="absolute right-4 top-1/2 -translate-y-1/2 text-yellow-500 w-6 h-6" />
                        </div>
                        <button
                            onClick={handleCheckServiceability}
                            className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-6 py-4 rounded-xl shadow-lg transition-transform hover:scale-105 w-full sm:w-auto"
                        >
                            Check
                        </button>
                    </div>

                    {loading && (
                        <p className="mt-6 text-gray-500 text-lg animate-pulse">Checking serviceability...</p>
                    )}
                    {error && (
                        <p className="mt-6 text-red-500 font-medium text-lg">{error}</p>
                    )}

                    {/* Result Card */}
                    {result && (
                        <div className="mt-10 bg-white shadow-xl rounded-2xl p-6 sm:p-8 w-full max-w-md mx-auto border border-gray-200 animate-fadeIn">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                <MapPin className="w-6 h-6 text-yellow-500" />
                                {result.city}, {result.state}
                                <span className="ml-auto font-mono text-gray-500">{result.pincode}</span>
                            </h2>

                            <div className="flex flex-wrap gap-3">
                                {[
                                    { label: "COD", available: result.cod },
                                    { label: "Prepaid", available: result.prepaid },
                                    { label: "Pickup", available: result.pickup },
                                    { label: "FM", available: result.fm }
                                ].map(({ label, available }) => (
                                    <span
                                        key={label}
                                        className={`px-4 py-1 rounded-full text-sm font-semibold ${available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}
                                    >
                                        {label}
                                    </span>
                                ))}

                                {/* ODA Badge with special logic */}
                                <span
                                    className={`px-4 py-1 rounded-full text-sm font-semibold ${result.oda && result.oda2 && result.oda3 && result.oda4 ?
                                            'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}
                                >
                                    ODA
                                </span>
                            </div>

                            <p
                                className={`mt-6 text-center font-semibold ${result.oda && result.oda2 && result.oda3 && result.oda4
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                            >
                                {result.oda && result.oda2 && result.oda3 && result.oda4
                                    ? "All services available"
                                    : "ODA Charge Applicable"}
                            </p>
                        </div>
                    )}
                </div>

                {/* CTA Section */}
            </div>
            <CtaPickup />
        </>
    );
};

export default ServiceabilityPage;
